import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import { FileDownload } from "@mui/icons-material";
import { writeXlsxFile } from "../../helper/utils";

export type PendingDuplicateAction = "merge" | "new" | "skip";

// Field names are lowerCamelCase to match this app's apiClient: every response is run
// through toCamel(), which lowercases just the FIRST character of each key. That's a
// correct camelCase transform for fields like Licensee -> licensee, but NOT for
// all-caps acronyms (NPN -> nPN, not npn) — see readNpn/readFein below for those two.
export interface PendingDuplicateCandidate {
  partnerLincenseeInformationId: number;
  licensee?: string;
  licenseState?: string;
  licenseNumber?: string;
  npn?: string;
  nPN?: string;
  fein?: string;
  fEIN?: string;
  title?: string;
  mainLicensee?: boolean;
  entity?: boolean;
  status?: string;
  statusReason?: string;
  cancellationDate?: string;
}

export interface PendingDuplicateIncoming {
  partnersId?: number;
  licensee?: string;
  licenseState?: string;
  licenseNumber?: string;
  npn?: string;
  nPN?: string;
  fein?: string;
  fEIN?: string;
  title?: string;
  mainLicensee?: boolean;
  entity?: boolean;
  status?: string;
  statusReason?: string;
  cancellationDate?: string;
}

export interface PendingDuplicateRow {
  rowNumber: number;
  matchType?: string;
  data?: any;
  incoming: PendingDuplicateIncoming;
  existingCandidates: PendingDuplicateCandidate[];
  suggestedAction: PendingDuplicateAction | null;
  suggestedCandidateId: number | null;
}

export interface RowResolution {
  rowNumber: number;
  action: PendingDuplicateAction;
  targetLicenseeId?: number;
  // The same incoming row data already rendered in this dialog, echoed back so the
  // server can resolve this row without looking anything up by jobId — see the
  // comment on jobId below.
  incoming: PendingDuplicateIncoming;
}

interface LicenseMatrixPendingDuplicatesDialogProps {
  open: boolean;
  rows: PendingDuplicateRow[];
  // Identifies which import these rows belong to. This component never unmounts
  // (it's just toggled via `open`), so per-row selections must be reset explicitly
  // when a NEW import's rows arrive — otherwise a choice made for "row 2" of one
  // import bleeds into an unrelated "row 2" of the next, since rowNumber always
  // restarts at 1 for each file. Not needed for resolving (each RowResolution carries
  // its own `incoming` data), only for this reset-on-new-import behavior.
  jobId: string | null;
  onApply: (resolutions: RowResolution[]) => Promise<void> | void;
  onClose: () => void;
}

const displayValue = (v: any) => (v === null || v === undefined || v === "" ? "—" : String(v));

// toCamel() only lowercases the first character, so NPN/FEIN survive as nPN/fEIN, not
// npn/fein. Check every variant, matching the same defensive pattern already used for
// this exact quirk elsewhere in LicenseMatrix.tsx (handleImportCompletion's invalid
// records mapping).
const readNpn = (obj: any): string => obj?.nPN ?? obj?.npn ?? obj?.NPN ?? "";
const readFein = (obj: any): string => obj?.fEIN ?? obj?.fein ?? obj?.FEIN ?? "";

// Human-facing label for an existing-match candidate — license data only, no internal
// database id. Falls back to NPN, then a plain marker, for the rare case a candidate
// has neither populated (exactly the kind of blank row this feature exists to catch).
const candidateLabel = (c: PendingDuplicateCandidate | undefined): string => {
  if (!c) return "—";
  const licenseNumber = displayValue(c.licenseNumber);
  if (licenseNumber !== "—") return `License # ${licenseNumber}`;
  const npn = readNpn(c);
  if (npn) return `NPN ${npn}`;
  return "Existing match (no license #/NPN on file)";
};

const primaryCandidateFor = (row: PendingDuplicateRow): PendingDuplicateCandidate | undefined =>
  row.existingCandidates.find((c) => c.partnerLincenseeInformationId === row.suggestedCandidateId) ||
  row.existingCandidates[0];

export const LicenseMatrixPendingDuplicatesDialog: React.FC<LicenseMatrixPendingDuplicatesDialogProps> = ({
  open,
  rows,
  jobId,
  onApply,
  onClose,
}) => {
  // Per-row selections, keyed by rowNumber. Undefined -> falls back to the suggested
  // hint, else Skip. Skip is always a real, safe no-op: closing without touching
  // anything is equivalent to choosing Skip for every row.
  const [actions, setActions] = useState<Record<number, PendingDuplicateAction>>({});
  const [targets, setTargets] = useState<Record<number, number>>({});
  const [submitting, setSubmitting] = useState(false);

  // Reset selections only when the JOB changes (a genuinely new import) — not when
  // `rows` merely shrinks after resolving some of the current job's rows, which
  // should keep whatever the user already picked for what's left.
  useEffect(() => {
    setActions({});
    setTargets({});
  }, [jobId]);

  const getAction = (row: PendingDuplicateRow): PendingDuplicateAction =>
    actions[row.rowNumber] ?? row.suggestedAction ?? "skip";

  const getTarget = (row: PendingDuplicateRow): number | undefined =>
    targets[row.rowNumber] ?? row.suggestedCandidateId ?? row.existingCandidates[0]?.partnerLincenseeInformationId;

  const setAction = (rowNumber: number, action: PendingDuplicateAction) => {
    setActions((prev) => ({ ...prev, [rowNumber]: action }));
  };

  const setTarget = (rowNumber: number, targetId: number) => {
    setTargets((prev) => ({ ...prev, [rowNumber]: targetId }));
  };

  const applyToAll = (action: PendingDuplicateAction) => {
    const next: Record<number, PendingDuplicateAction> = {};
    rows.forEach((row) => {
      next[row.rowNumber] = action;
    });
    setActions(next);
  };

  const columns: GridColDef[] = useMemo(
    () => [
      { field: "rowNumber", headerName: "Row", width: 60 },
      { field: "incomingLicensee", headerName: "Incoming Licensee", flex: 1, minWidth: 140 },
      { field: "incomingState", headerName: "State", width: 70 },
      { field: "incomingLicenseNumber", headerName: "Incoming License #", width: 150 },
      { field: "incomingNpn", headerName: "Incoming NPN", width: 120 },
      { field: "existingLicensee", headerName: "Existing Licensee", flex: 1, minWidth: 140 },
      { field: "existingLicenseNumber", headerName: "Existing License #", width: 150 },
      { field: "existingNpn", headerName: "Existing NPN", width: 120 },
      { field: "otherCandidates", headerName: "Other matches", width: 110 },
      {
        field: "action",
        headerName: "Action",
        width: 180,
        sortable: false,
        renderCell: (params) => {
          const row = params.row.__source as PendingDuplicateRow;
          return (
            <Select
              size="small"
              fullWidth
              value={getAction(row)}
              onChange={(e: SelectChangeEvent) => setAction(row.rowNumber, e.target.value as PendingDuplicateAction)}
            >
              <MenuItem value="merge">Merge into existing</MenuItem>
              <MenuItem value="new">Import as new</MenuItem>
              <MenuItem value="skip">Skip</MenuItem>
            </Select>
          );
        },
      },
      {
        field: "mergeTarget",
        headerName: "Merge into",
        width: 170,
        sortable: false,
        renderCell: (params) => {
          const row = params.row.__source as PendingDuplicateRow;
          if (getAction(row) !== "merge") {
            return <span>—</span>;
          }
          // Only one candidate: nothing to pick, but still show what it's merging
          // into instead of leaving the cell blank (that read as broken, not "n/a").
          if (row.existingCandidates.length <= 1) {
            return <span>{candidateLabel(row.existingCandidates[0])}</span>;
          }
          return (
            <Select
              size="small"
              fullWidth
              value={getTarget(row) ?? ""}
              onChange={(e: SelectChangeEvent<number>) => setTarget(row.rowNumber, Number(e.target.value))}
            >
              {row.existingCandidates.map((c) => (
                // The id is still the Select's value (needed to know which row was
                // picked) — it's just not shown in the label a person reads.
                <MenuItem key={c.partnerLincenseeInformationId} value={c.partnerLincenseeInformationId}>
                  {candidateLabel(c)}
                </MenuItem>
              ))}
            </Select>
          );
        },
      },
    ],
    // Selections and rows both drive what each cell renders (current value + options).
    [actions, targets, rows]
  );

  const gridRows = useMemo(
    () =>
      rows.map((row) => {
        const primary = primaryCandidateFor(row);
        return {
          id: row.rowNumber,
          __source: row,
          rowNumber: row.rowNumber,
          incomingLicensee: displayValue(row.incoming.licensee),
          incomingState: displayValue(row.incoming.licenseState),
          incomingLicenseNumber: displayValue(row.incoming.licenseNumber),
          incomingNpn: displayValue(readNpn(row.incoming)),
          existingLicensee: displayValue(primary?.licensee),
          existingLicenseNumber: displayValue(primary?.licenseNumber),
          existingNpn: displayValue(readNpn(primary)),
          otherCandidates: row.existingCandidates.length > 1 ? `+${row.existingCandidates.length - 1} more` : "",
        };
      }),
    [rows]
  );

  const handleDownloadForReview = () => {
    const headerRow = [
      "Row Number",
      "Suggested Action",
      "Incoming Licensee",
      "Incoming State",
      "Incoming License #",
      "Incoming NPN",
      "Incoming FEIN",
      "Incoming Status",
      "Existing ID",
      "Existing Licensee",
      "Existing State",
      "Existing License #",
      "Existing NPN",
      "Existing FEIN",
      "Existing Status",
      "Other Matches",
    ];
    const exportRows = rows.map((row) => {
      const primary = primaryCandidateFor(row);
      return [
        row.rowNumber,
        row.suggestedAction ?? "",
        row.incoming.licensee ?? "",
        row.incoming.licenseState ?? "",
        row.incoming.licenseNumber ?? "",
        readNpn(row.incoming),
        readFein(row.incoming),
        row.incoming.status ?? "",
        primary?.partnerLincenseeInformationId ?? "",
        primary?.licensee ?? "",
        primary?.licenseState ?? "",
        primary?.licenseNumber ?? "",
        readNpn(primary),
        readFein(primary),
        primary?.status ?? "",
        Math.max(0, row.existingCandidates.length - 1),
      ];
    });
    const today = new Date().toISOString().slice(0, 10);
    writeXlsxFile([headerRow, ...exportRows], `PendingDuplicateLicensees_${today}.xlsx`);
  };

  const handleApply = async () => {
    setSubmitting(true);
    try {
      const resolutions: RowResolution[] = rows.map((row) => {
        const action = getAction(row);
        const resolution: RowResolution = { rowNumber: row.rowNumber, action, incoming: row.incoming };
        if (action === "merge") {
          resolution.targetLicenseeId = getTarget(row);
        }
        return resolution;
      });
      await onApply(resolutions);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>Possible Duplicate Licensees</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {rows.length} row(s) matched an existing Matrix row on the same Partner, Licensee, and License State, but
          the License Number/NPN didn't match exactly (blank, formatted differently, or genuinely different). These
          were held out of the import — choose Merge, Import as new, or Skip for each row below. Closing without
          applying leaves them pending; nothing here has been written yet.
        </Typography>
        <Box sx={{ display: "flex", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
          <Button size="small" variant="outlined" onClick={() => applyToAll("skip")}>
            Set all to Skip
          </Button>
          <Button size="small" variant="outlined" onClick={() => applyToAll("new")}>
            Set all to Import as new
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button size="small" startIcon={<FileDownload />} onClick={handleDownloadForReview}>
            Download for review
          </Button>
        </Box>
        <Box sx={{ height: 420, width: "100%" }}>
          <DataGridPro rows={gridRows} columns={columns} disableSelectionOnClick />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} color="inherit" disabled={submitting}>
          Close (resolve later)
        </Button>
        <Button onClick={handleApply} variant="contained" color="primary" disabled={submitting}>
          Apply Actions
        </Button>
      </DialogActions>
    </Dialog>
  );
};
